/**
 * simpli-header-menu-hover.js
 * Hover-Intent für das Desktop-Megamenü von Horizon (header-menu.js), ohne Core-Dateien zu ändern.
 *
 * Problem im Original (Horizon 4.2.0):
 * - Das <li> eines Menüpunkts schliesst sein Megamenü sofort über on:pointerleave="/deactivate".
 * - Das Untermenü wird per clip-path-Transition (0,36 s) eingeblendet. Der noch nicht freigegebene
 *   Teil ist für den Browser nicht treffbar, eine zügige Mausbewegung nach unten "verlässt" damit
 *   das <li> und das Menü klappt zu.
 * - Diagonale Bewegungen zu einer seitlichen Spalte streifen Nachbarpunkte (z. B. Pickleball, Outlet).
 *   Deren on:pointerenter="/activate" schliesst oder wechselt das offene Megamenü sofort.
 *
 * Lösung:
 * - Alle Listener hängen am window in der Capture-Phase und laufen damit vor dem Listener des Themes
 *   (component.js registriert on:-Attribute am document). Nur echte Maus-/Stift-Events werden abgefangen,
 *   Touch, Tastatur (blur, Escape) und die Klick-Logik des Themes bleiben unverändert.
 * - Verlässt der Pointer den offenen Menüpunkt, wird das Schliessen um CLOSE_DELAY_MS verzögert. Kommt
 *   der Pointer zurück oder liegt er nach Ablauf (wieder) im Menü, bleibt es offen. Sonst wird das
 *   Schliessen regulär über ein synthetisches pointerleave an das Theme übergeben.
 * - Betritt der Pointer bei offenem Megamenü einen anderen Menüpunkt, wird dessen Aktivierung um
 *   SWITCH_DELAY_MS verzögert und nur ausgeführt, wenn der Pointer dort noch liegt.
 * - Verlässt der Pointer das Browserfenster (relatedTarget null), schliesst das Menü wie im Original sofort.
 *
 * Konfiguration zur Laufzeit (z. B. in der Konsole): window.simpli.headerMenuHover.closeDelayMs / switchDelayMs
 *
 * Abhängigkeiten: Markup und Refs von Horizon header-menu.js (li.menu-list__list-item, ref="submenu[]",
 * ref="disclosure[]", ref="menuitem"). Keine Theme-Einstellungen, keine Metafelder.
 */
(() => {
  'use strict';

  window.simpli = window.simpli || {};
  if (window.simpli.headerMenuHover) return; // Doppeltes Laden verhindern

  const config = {
    closeDelayMs: 250, // Gnadenfrist, bevor ein verlassenes Megamenü schliesst
    switchDelayMs: 120, // Verweildauer auf einem Nachbarpunkt, bevor dorthin gewechselt wird
  };
  window.simpli.headerMenuHover = config;

  const ITEM_SELECTOR = 'li.menu-list__list-item';

  const state = {
    closeTimer: 0,
    switchTimer: 0,
    pointerX: 0,
    pointerY: 0,
  };

  /** Nur Maus und Stift, kein Touch (dort nutzt Horizon den Drawer). */
  function isMouseLike(event) {
    return event.pointerType === 'mouse' || event.pointerType === 'pen';
  }

  /**
   * Das Event-Target, wenn es selbst ein Menüpunkt der obersten Ebene ist, sonst null.
   * pointerenter/pointerleave feuern pro Element der Kette einzeln, deshalb zählt nur das <li> selbst
   * (sein pointerleave feuert erst, wenn der Pointer das <li> samt Untermenü komplett verlassen hat).
   * Overflow-Punkte ("Mehr") bleiben beim Theme.
   */
  function menuItemOf(target) {
    if (!(target instanceof Element) || !target.matches(ITEM_SELECTOR)) return null;
    if (!target.closest('header-menu')) return null;
    if (target.slot === 'more' || target.slot === 'overflow') return null;
    return target;
  }

  /** Hat der Menüpunkt ein Untermenü (Megamenü oder Dropdown)? */
  function hasSubmenu(item) {
    return !!item.querySelector(':scope > [ref="submenu[]"]');
  }

  /**
   * Menüpunkt, dessen Untermenü das Theme aktuell geöffnet hat. header-menu.js setzt aria-expanded="true"
   * auf dem Disclosure-Button (oder dem Link, wenn es kein Untermenü gibt). Punkte ohne Untermenü zählen
   * hier nicht als offen, weil nichts sichtbar ist.
   */
  function openItem(headerMenu) {
    for (const item of headerMenu.querySelectorAll(ITEM_SELECTOR)) {
      if (item.slot === 'more' || item.slot === 'overflow') continue;
      const expanded = item.querySelector(
        ':scope > .menu-list__trigger > [aria-expanded="true"], :scope > [ref="menuitem"][aria-expanded="true"]'
      );
      if (expanded && hasSubmenu(item)) return item;
    }
    return null;
  }

  /** Liegt der zuletzt gemeldete Pointer innerhalb des Elements (inkl. Untermenü)? */
  function pointerInside(element) {
    const hit = document.elementFromPoint(state.pointerX, state.pointerY);
    return !!hit && element.contains(hit);
  }

  function cancelClose() {
    clearTimeout(state.closeTimer);
    state.closeTimer = 0;
  }

  function cancelSwitch() {
    clearTimeout(state.switchTimer);
    state.switchTimer = 0;
  }

  /** Synthetisches Event direkt am <li>, damit component.js den on:-Handler des Themes regulär ausführt. */
  function dispatchToTheme(item, type) {
    item.dispatchEvent(
      new PointerEvent(type, { bubbles: false, cancelable: false, pointerType: 'mouse', relatedTarget: null })
    );
  }

  function scheduleClose(item) {
    cancelClose();
    state.closeTimer = window.setTimeout(() => {
      state.closeTimer = 0;
      const headerMenu = item.closest('header-menu');
      if (!headerMenu || openItem(headerMenu) !== item) return; // inzwischen gewechselt oder geschlossen
      if (pointerInside(item)) return; // Pointer liegt (wieder) im Menü, z. B. nach Ende der Einblend-Animation
      dispatchToTheme(item, 'pointerleave');
    }, config.closeDelayMs);
  }

  function scheduleSwitch(item) {
    cancelSwitch();
    state.switchTimer = window.setTimeout(() => {
      state.switchTimer = 0;
      if (!item.isConnected || !pointerInside(item)) return; // Pointer ist nur durchgefahren
      dispatchToTheme(item, 'pointerenter');
    }, config.switchDelayMs);
  }

  window.addEventListener(
    'pointermove',
    (event) => {
      state.pointerX = event.clientX;
      state.pointerY = event.clientY;
    },
    { capture: true, passive: true }
  );

  window.addEventListener(
    'pointerenter',
    (event) => {
      if (!event.isTrusted || !isMouseLike(event)) return;
      const item = menuItemOf(event.target);
      if (!item) return;
      const current = openItem(item.closest('header-menu'));
      if (!current) return; // Nichts offen: Theme öffnet sofort wie bisher
      if (current === item) {
        // Zurück im offenen Punkt oder in seinem Untermenü: geplantes Schliessen und Wechseln verwerfen
        cancelClose();
        cancelSwitch();
        return;
      }
      // Anderer Punkt bei offenem Megamenü: erst wechseln, wenn der Pointer dort kurz verweilt
      event.stopImmediatePropagation();
      scheduleSwitch(item);
    },
    true
  );

  window.addEventListener(
    'pointerleave',
    (event) => {
      if (!event.isTrusted || !isMouseLike(event)) return;
      const item = menuItemOf(event.target);
      if (!item) return;
      const current = openItem(item.closest('header-menu'));
      if (!current) return; // Nichts offen: Theme-Verhalten unverändert
      if (event.relatedTarget === null) {
        // Pointer hat das Fenster verlassen: sofort schliessen wie im Original
        cancelClose();
        cancelSwitch();
        return;
      }
      event.stopImmediatePropagation();
      if (current === item) {
        scheduleClose(item);
      } else {
        cancelSwitch(); // Nachbarpunkt nur gestreift
      }
    },
    true
  );
})();
